/**
 * Local-storage mirror for the active-party selection.
 *
 * Spec: active-party-top-bar — Requirement 5.3. A per-tenant map under the key
 * `sm_active_party` carries the buyer/supplier id + code + name so the top bar
 * can render the last selection *with names* before the server data resolves,
 * and so the same device recalls a per-tenant selection. The authoritative
 * store is `UserPreference.ActiveBuyerPartyId` / `ActiveSupplierPartyId`; this
 * mirror is a same-device convenience (mirrors the `sm_active_tenant` precedent).
 *
 * Pure module — no store import, safe for both the persist listener and the
 * selector component to import.
 */

const ACTIVE_PARTY_KEY = 'sm_active_party';

/** A buyer/supplier reference persisted with display fields for instant render. */
export interface StoredPartyRef {
  partyId: number;
  partyCode: string;
  partyName: string;
}

/** The stored (buyer, supplier) pair for one tenant. */
export interface StoredActiveParty {
  buyer: StoredPartyRef | null;
  supplier: StoredPartyRef | null;
}

/** The whole `sm_active_party` value — keyed by tenant id (stringified). */
type ActivePartyMap = Record<string, StoredActiveParty>;

function isPartyRef(v: unknown): v is StoredPartyRef {
  return (
    !!v &&
    typeof v === 'object' &&
    typeof (v as StoredPartyRef).partyId === 'number' &&
    typeof (v as StoredPartyRef).partyCode === 'string' &&
    typeof (v as StoredPartyRef).partyName === 'string'
  );
}

function readMap(): ActivePartyMap {
  try {
    if (typeof window === 'undefined') return {};
    const raw = localStorage.getItem(ACTIVE_PARTY_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as ActivePartyMap) : {};
  } catch {
    // localStorage unavailable or corrupt — treat as empty.
    return {};
  }
}

/** Reads the stored (buyer, supplier) pair for a tenant, or null when absent. */
export function readActiveParty(tenantId: number): StoredActiveParty | null {
  const entry = readMap()[String(tenantId)];
  if (!entry || typeof entry !== 'object') return null;
  return {
    buyer: isPartyRef(entry.buyer) ? entry.buyer : null,
    supplier: isPartyRef(entry.supplier) ? entry.supplier : null,
  };
}

/** The persisted buyer id for a tenant — fed as `persistedId` into resolution. */
export function readPersistedBuyerId(tenantId: number): number | null {
  return readActiveParty(tenantId)?.buyer?.partyId ?? null;
}

/** The persisted supplier id for a tenant — fed as `persistedId` into resolution. */
export function readPersistedSupplierId(tenantId: number): number | null {
  return readActiveParty(tenantId)?.supplier?.partyId ?? null;
}

/** Writes the (buyer, supplier) pair for a tenant. Never throws (Req 5.5). */
export function writeActiveParty(tenantId: number, value: StoredActiveParty): void {
  try {
    if (typeof window === 'undefined') return;
    const map = readMap();
    map[String(tenantId)] = value;
    localStorage.setItem(ACTIVE_PARTY_KEY, JSON.stringify(map));
  } catch {
    // localStorage unavailable — the in-memory selection still stands (Req 5.5).
  }
}

/** Drops the entire mirror — called on logout so a new user starts clean. */
export function clearActivePartyStorage(): void {
  try {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(ACTIVE_PARTY_KEY);
  } catch {
    // localStorage unavailable — nothing to clear.
  }
}
